import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
    TablePagination,
    TextField,
    InputAdornment,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { IconMail, IconRefresh, IconSend, IconSearch, IconCheck, IconEye } from '@tabler/icons';
import MainCard from 'ui-component/cards/MainCard';

const GET_INVITATION_DATA = gql`
    query GetInvitationData($eventId: String!, $role: ROLE!) {
        invitationData(eventId: $eventId, role: $role) {
            serial
            data
            isAccepted
            isInvitationSend
            created
            status
            uploadedBy
            uploadedByEmail
            invitationStatus
            responseTimestamp
            isConfirmationSent
        }
    }
`;

const GET_CONFIRMATION_EMAIL_TEMPLATE = gql`
    query GetConfirmationEmailTemplate($eventId: String!, $role: ROLE!) {
        confirmationEmailTemplate(eventId: $eventId, role: $role) {
            subject
            body
            isHtml
        }
    }
`;

const SEND_INVITATION_EMAIL = gql`
    mutation SendInvitationEmail($eventId: String!, $role: ROLE!, $invitationEmails: [String!]!) {
        sendInvitationEmail(eventId: $eventId, role: $role, invitationEmails: $invitationEmails) {
            success
            totalSent
            totalFailed
            message
        }
    }
`;

export default function SendConfirmationEmails({ eventId, role }) {
    const [selectedInvitations, setSelectedInvitations] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const { data, loading, error, refetch } = useQuery(GET_INVITATION_DATA, {
        variables: { eventId, role },
    });

    const { data: templateData, loading: templateLoading } = useQuery(
        GET_CONFIRMATION_EMAIL_TEMPLATE,
        {
            variables: { eventId, role },
        }
    );

    const [sendInvitationEmail, { loading: sendingEmail }] = useMutation(SEND_INVITATION_EMAIL, {
        onCompleted: (data) => {
            setToast({
                open: true,
                message: data.sendInvitationEmail.message,
                severity: 'success',
            });
            setSelectedInvitations([]);
            refetch();
        },
        onError: (e) => {
            setToast({
                open: true,
                message: e.message || 'Failed to send invitation emails',
                severity: 'error',
            });
        },
    });

    // Get invitation data from query result
    const invitationData = data?.invitationData || [];

    // Filter data
    const filteredData = invitationData.filter((invitation) => {
        const invitationInfo = JSON.parse(invitation.data || '{}');

        // Search filter
        const searchMatch =
            searchTerm === '' ||
            Object.values(invitationInfo).some((value) =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            invitation.uploadedByEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            invitation.uploadedBy?.toLowerCase().includes(searchTerm.toLowerCase());

        // Status filter
        let statusMatch = true;
        if (statusFilter === 'pending') {
            statusMatch = !invitation.isInvitationSend;
        } else if (statusFilter === 'sent') {
            statusMatch = invitation.isInvitationSend;
        } else if (statusFilter === 'accepted') {
            statusMatch = invitation.isAccepted;
        } else if (statusFilter === 'not_accepted') {
            statusMatch = invitation.isInvitationSend && !invitation.isAccepted;
        }

        return searchMatch && statusMatch;
    });

    // Pagination logic
    const paginatedData =
        rowsPerPage === 'all'
            ? filteredData
            : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Reset page when filters change
    React.useEffect(() => {
        setPage(0);
    }, [searchTerm, statusFilter]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Select invitations that haven't been sent yet OR sent but not accepted
            const selectableInvitations = paginatedData
                .filter(
                    (invitation) =>
                        !invitation.isInvitationSend ||
                        (invitation.isInvitationSend && !invitation.isAccepted)
                )
                .map((invitation) => invitation.serial);
            setSelectedInvitations(selectableInvitations);
        } else {
            setSelectedInvitations([]);
        }
    };

    const handleSelectOne = (event, invitationSerial) => {
        if (event.target.checked) {
            setSelectedInvitations([...selectedInvitations, invitationSerial]);
        } else {
            setSelectedInvitations(selectedInvitations.filter((id) => id !== invitationSerial));
        }
    };

    const handlePreviewEmail = async () => {
        if (selectedInvitations.length === 0) {
            setToast({
                open: true,
                message: 'Please select at least one invitation to preview email.',
                severity: 'warning',
            });
            return;
        }

        // Check if template exists
        const template = templateData?.confirmationEmailTemplate;
        if (!template || !template.subject || !template.body) {
            setToast({
                open: true,
                message: 'Please setup Interested Email template first to send invitations.',
                severity: 'error',
            });
            return;
        }

        // Get the first selected invitation for preview
        const firstInvitation = invitationData.find((inv) => inv.serial === selectedInvitations[0]);
        const invitationInfo = JSON.parse(firstInvitation.data || '{}');

        // Generate interested link
        const interestedLink = `https://eventegration.com/confirmation/index.php?userdetail=${firstInvitation.serial}&eventId=${eventId}&role=${role}`;

        // Parse template with variables
        let emailBody = template.body;
        let emailSubject = template.subject;

        // Replace interested link variable
        emailBody = emailBody.replace(/\$interested_link/g, interestedLink);
        emailSubject = emailSubject.replace(/\$interested_link/g, interestedLink);

        // Replace other variables
        for (const [key, value] of Object.entries(invitationInfo)) {
            if (value !== null && value !== undefined) {
                const regex = new RegExp(`\\$${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
                emailBody = emailBody.replace(regex, value);
                emailSubject = emailSubject.replace(regex, value);
            }
        }

        setPreviewData({
            subject: emailSubject,
            body: emailBody,
            isHtml: template.isHtml,
            recipientEmail: invitationInfo.Email,
            recipientName:
                invitationInfo['Full Name'] ||
                invitationInfo['Name'] ||
                invitationInfo['First Name'] ||
                'Guest',
            totalRecipients: selectedInvitations.length,
        });

        setShowPreview(true);
    };

    const handleSendConfirmation = async () => {
        if (selectedInvitations.length === 0) {
            setToast({
                open: true,
                message: 'Please select at least one invitation to send invitation email',
                severity: 'warning',
            });
            return;
        }

        // Check if template exists
        const template = templateData?.confirmationEmailTemplate;
        if (!template || !template.subject || !template.body) {
            setToast({
                open: true,
                message: 'Please setup Interested Email template first to send invitations.',
                severity: 'error',
            });
            return;
        }

        // Get email addresses for selected invitations
        const selectedInvitationData = invitationData.filter((invitation) =>
            selectedInvitations.includes(invitation.serial)
        );

        const emailAddresses = selectedInvitationData
            .map((invitation) => {
                const invitationInfo = JSON.parse(invitation.data || '{}');
                return invitationInfo.Email;
            })
            .filter((email) => email); // Filter out empty emails

        if (emailAddresses.length === 0) {
            setToast({
                open: true,
                message: 'No valid email addresses found in selected invitations',
                severity: 'error',
            });
            return;
        }

        // Show preview confirmation modal
        setPreviewData({
            subject: template.subject,
            body: template.body,
            isHtml: template.isHtml,
            recipientEmails: emailAddresses,
            totalRecipients: emailAddresses.length,
        });
        setShowPreview(true);
    };

    const handleConfirmSend = async () => {
        if (!previewData) return;

        const selectedInvitationData = invitationData.filter((invitation) =>
            selectedInvitations.includes(invitation.serial)
        );

        const emailAddresses = selectedInvitationData
            .map((invitation) => {
                const invitationInfo = JSON.parse(invitation.data || '{}');
                return invitationInfo.Email;
            })
            .filter((email) => email);

        try {
            await sendInvitationEmail({
                variables: {
                    eventId,
                    role,
                    invitationEmails: emailAddresses,
                },
            });
            setShowPreview(false);
            setSelectedInvitations([]);
            refetch();
        } catch (error) {
            console.error('Error sending invitation emails:', error);
        }
    };

    if (loading) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <LinearProgress />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading invitations...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (error) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">Error loading invitations: {error.message}</Alert>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard>
            <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Send Invitations
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Send invitation emails to users who have not received them yet. Users
                            will receive an email with an interested link to express their interest.
                        </Typography>
                    </Box>

                    {/* Template Status Warning */}
                    {templateData?.confirmationEmailTemplate &&
                        (!templateData.confirmationEmailTemplate.subject ||
                            !templateData.confirmationEmailTemplate.body) && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Interested Email Template Not Configured</strong>
                                    <br />
                                    Please setup the Interested Email template first to send
                                    invitations. Go to the Interested Email Setup tab to configure
                                    your email template.
                                </Typography>
                            </Alert>
                        )}
                    {/* Row 1: Enhanced Statistics Cards (md screens and above) */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                    }}
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconMail size={28} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ fontWeight: 'bold', color: 'white' }}
                                                >
                                                    {
                                                        invitationData.filter(
                                                            (inv) => !inv.isInvitationSend
                                                        ).length
                                                    }
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                                >
                                                    Pending Invitations
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)',
                                    }}
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconSend size={28} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ fontWeight: 'bold', color: 'white' }}
                                                >
                                                    {
                                                        invitationData.filter(
                                                            (inv) => inv.isInvitationSend
                                                        ).length
                                                    }
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                                >
                                                    Invitations Sent
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(67, 233, 123, 0.3)',
                                    }}
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconCheck size={28} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ fontWeight: 'bold', color: 'white' }}
                                                >
                                                    {
                                                        invitationData.filter(
                                                            (inv) =>
                                                                inv.invitationStatus === 'accepted'
                                                        ).length
                                                    }
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                                >
                                                    Accepted
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 20px rgba(250, 112, 154, 0.3)',
                                    }}
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconMail size={28} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ fontWeight: 'bold', color: 'white' }}
                                                >
                                                    {
                                                        invitationData.filter(
                                                            (inv) =>
                                                                inv.invitationStatus === 'rejected'
                                                        ).length
                                                    }
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                                >
                                                    Rejected
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                                        color: '#333',
                                        boxShadow: '0 4px 20px rgba(168, 237, 234, 0.3)',
                                    }}
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                    backdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <IconRefresh size={28} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ fontWeight: 'bold', color: '#333' }}
                                                >
                                                    {
                                                        invitationData.filter(
                                                            (inv) =>
                                                                inv.invitationStatus ===
                                                                    'pending' ||
                                                                !inv.invitationStatus
                                                        ).length
                                                    }
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'rgba(51, 51, 51, 0.7)' }}
                                                >
                                                    Pending Response
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Row 2: Search and Filter */}
                    <Card>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        placeholder="Search invitations..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconSearch size={20} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            label="Status"
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="sent">Sent</MenuItem>
                                            <MenuItem value="accepted">Accepted</MenuItem>
                                            <MenuItem value="not_accepted">
                                                Sent (Not Accepted)
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Row 3: Action Buttons */}
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: { xs: 'stretch', md: 'flex-start' },
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    startIcon={<IconEye />}
                                    onClick={handlePreviewEmail}
                                    disabled={
                                        selectedInvitations.length === 0 ||
                                        !templateData?.confirmationEmailTemplate?.subject ||
                                        !templateData?.confirmationEmailTemplate?.body
                                    }
                                    sx={{
                                        minWidth: { xs: '100%', sm: '140px' },
                                        mb: { xs: 1, sm: 0 },
                                        height: '48px',
                                        borderRadius: '12px',
                                        border: '2px solid #4facfe',
                                        color: '#4facfe',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            border: '2px solid #4facfe',
                                            backgroundColor: 'rgba(79, 172, 254, 0.1)',
                                        },
                                    }}
                                >
                                    Preview Email
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<IconSend />}
                                    onClick={handleSendConfirmation}
                                    disabled={
                                        selectedInvitations.length === 0 ||
                                        sendingEmail ||
                                        !templateData?.confirmationEmailTemplate?.subject ||
                                        !templateData?.confirmationEmailTemplate?.body
                                    }
                                    sx={{
                                        minWidth: { xs: '100%', sm: '180px' },
                                        mb: { xs: 1, sm: 0 },
                                        height: '48px',
                                        borderRadius: '12px',
                                        background:
                                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            background:
                                                'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                                        },
                                        '&:disabled': {
                                            background: '#ccc',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    {sendingEmail
                                        ? 'Sending...'
                                        : `Send Invitations (${selectedInvitations.length})`}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<IconRefresh />}
                                    onClick={() => refetch()}
                                    sx={{
                                        minWidth: { xs: '100%', sm: '120px' },
                                        height: '48px',
                                        borderRadius: '12px',
                                        border: '2px solid #43e97b',
                                        color: '#43e97b',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            border: '2px solid #43e97b',
                                            backgroundColor: 'rgba(67, 233, 123, 0.1)',
                                        },
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Row 4: Data Table */}
                    <Card>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={
                                                    paginatedData.filter(
                                                        (inv) =>
                                                            !inv.isInvitationSend ||
                                                            (inv.isInvitationSend &&
                                                                !inv.isAccepted)
                                                    ).length > 0 &&
                                                    selectedInvitations.length ===
                                                        paginatedData.filter(
                                                            (inv) =>
                                                                !inv.isInvitationSend ||
                                                                (inv.isInvitationSend &&
                                                                    !inv.isAccepted)
                                                        ).length
                                                }
                                                indeterminate={
                                                    selectedInvitations.length > 0 &&
                                                    selectedInvitations.length <
                                                        paginatedData.filter(
                                                            (inv) =>
                                                                !inv.isInvitationSend ||
                                                                (inv.isInvitationSend &&
                                                                    !inv.isAccepted)
                                                        ).length
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                display: { xs: 'none', md: 'table-cell' },
                                            }}
                                        >
                                            Company
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                display: { xs: 'none', sm: 'table-cell' },
                                            }}
                                        >
                                            Uploaded Date
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.map((invitation) => {
                                        const invitationInfo = JSON.parse(invitation.data || '{}');
                                        const isSelected = selectedInvitations.includes(
                                            invitation.serial
                                        );

                                        return (
                                            <TableRow key={invitation.serial} hover>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        disabled={
                                                            invitation.isInvitationSend &&
                                                            invitation.isAccepted
                                                        }
                                                        onChange={(event) =>
                                                            handleSelectOne(
                                                                event,
                                                                invitation.serial
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {invitationInfo['Full Name'] ||
                                                        invitationInfo['Name'] ||
                                                        invitationInfo['First Name'] +
                                                            ' ' +
                                                            invitationInfo['Last Name'] ||
                                                        'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    {invitationInfo.Email || 'N/A'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        display: { xs: 'none', md: 'table-cell' },
                                                    }}
                                                >
                                                    {invitationInfo.Company ||
                                                        invitationInfo.Organization ||
                                                        invitationInfo['Company Name'] ||
                                                        invitationInfo['company'] ||
                                                        invitationInfo['organization'] ||
                                                        invitationInfo['organization name'] ||
                                                        invitationInfo['Organization'] ||
                                                        invitationInfo['Organization Name'] ||
                                                        'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={invitation.status || 'new'}
                                                        color="default"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        display: { xs: 'none', sm: 'table-cell' },
                                                    }}
                                                >
                                                    {invitation.created &&
                                                    invitation.created !== 'Invalid Date'
                                                        ? new Date(
                                                              parseInt(invitation.created)
                                                          ).toLocaleDateString()
                                                        : 'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            invitation.invitationStatus ===
                                                            'accepted'
                                                                ? 'Accepted'
                                                                : invitation.invitationStatus ===
                                                                  'rejected'
                                                                ? 'Rejected'
                                                                : invitation.invitationStatus ===
                                                                  'pending'
                                                                ? 'Pending'
                                                                : invitation.isInvitationSend
                                                                ? 'Sent'
                                                                : 'Not Sent'
                                                        }
                                                        color={
                                                            invitation.invitationStatus ===
                                                            'accepted'
                                                                ? 'success'
                                                                : invitation.invitationStatus ===
                                                                  'rejected'
                                                                ? 'error'
                                                                : invitation.invitationStatus ===
                                                                  'pending'
                                                                ? 'warning'
                                                                : invitation.isInvitationSend
                                                                ? 'info'
                                                                : 'default'
                                                        }
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, { label: 'All', value: 'all' }]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage === 'all' ? -1 : rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(event.target.value);
                                setPage(0);
                            }}
                        />
                    </Card>
                </Stack>
            </Box>

            {/* Toast Notification */}
            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert
                    onClose={() => setToast({ ...toast, open: false })}
                    severity={toast.severity}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            {/* Email Preview Modal */}
            {showPreview && previewData && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <Paper
                        sx={{
                            p: 3,
                            maxWidth: { xs: '95%', sm: '700px', md: '800px' },
                            maxHeight: '85vh',
                            overflow: 'auto',
                            width: '90%',
                        }}
                    >
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="h6">Email Preview</Typography>
                                <Button onClick={() => setShowPreview(false)}>Close</Button>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Total Recipients:</strong> {previewData.totalRecipients}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Recipients:</strong>
                                </Typography>
                                <Box
                                    sx={{
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                        border: '1px solid #e0e0e0',
                                        p: 1,
                                        borderRadius: 1,
                                    }}
                                >
                                    {previewData.recipientEmails?.map((email, index) => (
                                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                            {index + 1}. {email}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Subject:</strong> {previewData.subject}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Body:</strong>
                                </Typography>
                                {previewData.isHtml ? (
                                    <Box
                                        sx={{
                                            border: '1px solid #ccc',
                                            p: 2,
                                            borderRadius: 1,
                                            minHeight: 200,
                                            backgroundColor: '#f9f9f9',
                                        }}
                                        dangerouslySetInnerHTML={{ __html: previewData.body }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            border: '1px solid #ccc',
                                            p: 2,
                                            borderRadius: 1,
                                            whiteSpace: 'pre-wrap',
                                            backgroundColor: '#f9f9f9',
                                        }}
                                    >
                                        {previewData.body}
                                    </Box>
                                )}
                            </Box>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<IconSend />}
                                    onClick={handleConfirmSend}
                                    color="primary"
                                    disabled={sendingEmail}
                                >
                                    {sendingEmail ? 'Sending...' : 'Confirm & Send Email'}
                                </Button>
                                <Button variant="outlined" onClick={() => setShowPreview(false)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
            )}
        </MainCard>
    );
}
